import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { onUpdate } from '../../helpers/reactQuery';
import {
  REACT_QUERY_GET_SIMPLE_ORDERS_FOR_ADMIN,
  REACT_QUERY_GET_SIMPLE_ORDERS,
  REACT_QUERY_GET_SIMPLE_ORDER_BY_ID,
  REACT_QUERY_GET_SIMPLE_ORDER_BY_ID_FOR_ADMIN,
} from '../constants';
import simpleOrderFactory from '../factories/simpleOrder';

/**
 * Create a simple order from cart
 */
export const useCreateSimpleOrder = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation((data) => simpleOrderFactory.createSimpleOrder(data), {
    ...options,
    onSuccess: (data, variables, context) => {
      // Invalidate simple orders list for admin
      queryClient.invalidateQueries([REACT_QUERY_GET_SIMPLE_ORDERS_FOR_ADMIN]);

      // Call custom onSuccess if provided
      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
  });
};

/**
 * Get simple orders for customer
 */
export const useGetSimpleOrders = (variables, options = {}) => {
  return useQuery(
    [REACT_QUERY_GET_SIMPLE_ORDERS, variables],
    () => simpleOrderFactory.getSimpleOrders(variables),
    options
  );
};

/**
 * Get simple order by ID (customer view)
 */
export const useGetSimpleOrderById = (id, options = {}) => {
  return useQuery(
    [REACT_QUERY_GET_SIMPLE_ORDER_BY_ID, id],
    () => simpleOrderFactory.getSimpleOrderById(id),
    {
      enabled: !!id,
      ...options,
    }
  );
};

/**
 * Get simple order by ID (admin view)
 */
export const useGetSimpleOrderByIdForAdmin = (id, options = {}) => {
  return useQuery(
    [REACT_QUERY_GET_SIMPLE_ORDER_BY_ID_FOR_ADMIN, id],
    () => simpleOrderFactory.getSimpleOrderByIdForAdmin(id),
    {
      enabled: !!id,
      ...options,
    }
  );
};

/**
 * Get simple orders for admin with filters
 */
export const useGetSimpleOrdersForAdmin = (variables, options = {}) => {
  return useQuery(
    [REACT_QUERY_GET_SIMPLE_ORDERS_FOR_ADMIN, variables],
    () => simpleOrderFactory.getSimpleOrdersForAdmin(variables),
    options
  );
};

/**
 * Update simple order status (admin only)
 */
export const useUpdateSimpleOrderStatus = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation(({ id, data }) => simpleOrderFactory.updateSimpleOrderStatus(id, data), {
    ...options,
    onSuccess: (data, variables, context) => {
      // Update cached data
      onUpdate(
        queryClient,
        [REACT_QUERY_GET_SIMPLE_ORDER_BY_ID_FOR_ADMIN, variables.id],
        data.simpleOrder
      );
      onUpdate(queryClient, [REACT_QUERY_GET_SIMPLE_ORDER_BY_ID, variables.id], data.simpleOrder);

      // Invalidate lists
      queryClient.invalidateQueries([REACT_QUERY_GET_SIMPLE_ORDERS_FOR_ADMIN]);

      // Call custom onSuccess if provided
      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
  });
};

/**
 * Update simple order notes (admin only)
 */
export const useUpdateSimpleOrderNotes = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation(({ id, data }) => simpleOrderFactory.updateSimpleOrderNotes(id, data), {
    ...options,
    onSuccess: (data, variables, context) => {
      // Update cached data
      onUpdate(
        queryClient,
        [REACT_QUERY_GET_SIMPLE_ORDER_BY_ID_FOR_ADMIN, variables.id],
        data.simpleOrder
      );
      onUpdate(queryClient, [REACT_QUERY_GET_SIMPLE_ORDER_BY_ID, variables.id], data.simpleOrder);

      // Call custom onSuccess if provided
      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
  });
};
